import { colors, cssThemedValue } from '@creditornot/cb-ingredients';
import { useMutation } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { remove } from 'ramda';
import React, { CSSProperties, useCallback, useContext, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import * as api from 'apis/restaurant-api/merchant-admin/menu2/api';
import { Category, Item } from 'apis/restaurant-api/merchant-admin/menu2/types';
import { ConfirmModal } from 'common/components';
import { WideAreaCheckbox } from 'common/components';
import ErrorToastContent from 'common/components/ErrorToastContent';
import MonetaryAmount from 'common/formatting/MonetaryAmount';
import { useResizeObserver } from 'common/hooks';
import { useLocaleUtils } from 'common/hooks';
import { Typography as T } from 'common/typography';
import { reportItemEnabledToggle, reportItemDelete } from 'modules/menu/behavioral';
import LazyLoadedThumbnail from 'modules/menu/components/LazyLoadedThumbnail';
import { MenuPermissionsContext } from 'modules/menu/components/MenuPermissionsContext';
import { MENU_MOBILE_BREAKPOINT, WOLT_SPECIALS_CATEGORY_ID } from 'modules/menu/constants';
import { useItemActionTracking } from 'modules/menu/hooks/telemetryHooks';
import { transformImagesApiToFormValues } from 'modules/menu/items/transformers';
import useMenuData from 'modules/menu/useMenuData';

import ItemActions from './ItemActions';
import ItemTags from './ItemTags';
import { MenuLocaleContext } from '../../../../MenuLocaleContext';
import { ItemQuantity } from '../../../types';
import EmptyThumbnail from '../../EmptyThumbnail';
import { HeaderImageUpload, HeaderImageUploadThumbnail } from '../HeaderImageUpload';
import { getPurchasabilityValidationErrors } from '../utils';

type Props = {
  style?: CSSProperties;
  category: Category | null;
  className?: string;
  hidden?: boolean;
  item: Item;
  itemInventoryCount: ItemQuantity | undefined;
  onEnableFail?: () => void;
  onEnableSuccess?: () => void;
  onSelect?: (itemId: string) => void;
  onDeselect?: (itemId: string) => void;
  active?: boolean;
  selected?: boolean;
  disabledSelection?: boolean;
  onClick?: (event: React.MouseEvent<Element, MouseEvent>, itemId: string) => void;
  onCopy?: (itemId: string, copyAsSpecial: boolean) => void;
};

type WeightBasedItemQuantityProps = {
  itemInventoryCount: ItemQuantity;
};

const Root = styled.div<{ busy: boolean }>`
  background-color: ${cssThemedValue(
    'surfaceMain',
  )}; /* Set wrapper background color to solid color so hover style doesn't make it transparent */
  border-block-end: 1px solid ${cssThemedValue('borderPrimary')};
  cursor: pointer;
  opacity: ${({ busy }) => (busy ? 0.4 : 1)};
`;

const Header = styled.div<{ hidden?: boolean; selected?: boolean; active?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  min-height: 104px;
  background-color: ${({ active, selected }) => {
    if (active) {
      return colors.wolt8;
    }
    if (selected) {
      return colors.wolt4;
    }
    return cssThemedValue('surfaceMain');
  }};
  opacity: ${({ hidden }) => (hidden ? 0 : 1)};
  transition: background-color 100ms;

  &:hover {
    background-color: ${({ selected }) =>
      selected ? colors.wolt8 : cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
`;

const Left = styled.div<{ hasMargin: boolean }>`
  display: flex;
  align-items: center;
  overflow: hidden;
  margin-inline-start: ${({ hasMargin }) => (hasMargin ? '24px' : 0)};
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: ${({ hasMargin }) => (hasMargin ? '16px' : 0)};
  }
`;

const Right = React.memo(styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;
  padding: 24px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    padding: 16px;
  }
`);

const StyledWideAreaCheckbox = styled(WideAreaCheckbox)`
  margin-inline-start: 8px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  margin-inline-end: 16px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-end: 8px;
  }
`;

const NameAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  white-space: nowrap;
  padding: 16px 0;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    padding: 8px 0;
  }
`;

const NameAndTags = styled.div`
  display: flex;
  margin-block-end: 4px;
  flex-direction: row;
  align-items: center;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-block-end: 0;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
  }
`;

const Name = styled(T.Body3).attrs({ fontWeight: 'bold' })`
  align-self: center;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${cssThemedValue('textPrimary')};
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    align-self: normal;
  }
`;

const StyledTags = styled(ItemTags)`
  margin-inline-start: 12px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const Quantity = styled(T.Body3)`
  color: ${cssThemedValue('textSecondary')};
  margin-inline-start: 12px;
  @media (max-width: ${MENU_MOBILE_BREAKPOINT}px) {
    margin-inline-start: 0;
  }
`;

const Description = styled(T.Body3)`
  color: ${cssThemedValue('textSecondary')};
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PriceWrapper = styled.div`
  margin-inline-end: 12px;
  display: flex;
  color: ${cssThemedValue('textPrimary')};
`;

const Price = styled(T.Body3)``;

const OriginalPrice = styled(T.Body3)`
  text-decoration: line-through;
`;

const DiscountPrice = styled(T.Body3)`
  color: ${colors.strawberry100};
  margin-inline-end: 12px;
`;

export const WeightBasedItemAvailableQuantity = React.memo(({ itemInventoryCount }: WeightBasedItemQuantityProps) => {
  return itemInventoryCount.balance < 1000 ? (
    <>
      {itemInventoryCount.available}/
      <FormattedNumber value={itemInventoryCount.balance} style="unit" unit="gram" unitDisplay="narrow" />
    </>
  ) : (
    <>
      {itemInventoryCount.available / 1000}/
      <FormattedNumber value={itemInventoryCount.balance / 1000} style="unit" unit="kilogram" unitDisplay="narrow" />
    </>
  );
});

const ItemHeader = ({
  style,
  category,
  className,
  hidden,
  item,
  itemInventoryCount,
  onEnableFail,
  onEnableSuccess,
  onDeselect,
  onSelect,
  active,
  selected,
  disabledSelection,
  onClick,
  onCopy,
}: Props) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { primaryLanguage } = useContext(MenuLocaleContext);
  const menuPermissions = useContext(MenuPermissionsContext);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const name = item.name.find(t => t.lang === primaryLanguage) || item.name[0];
  const description = item.description.find(t => t.lang === primaryLanguage) || item.description[0];

  const { items, options, updateMenuData } = useMenuData();

  const purchasabilityErrors = useMemo(() => getPurchasabilityValidationErrors(options, item), [options, item]);

  const { width } = useResizeObserver(rootRef);

  /* 24px account for the left navigation when collapsed */
  const isNarrowView = useMemo(() => width !== undefined && width <= MENU_MOBILE_BREAKPOINT - 24, [width]);

  const isBinaryInventory = useMemo(
    () => !item.inventory_mode || item.inventory_mode === 'forced_out_of_stock',
    [item.inventory_mode],
  );

  const { trackRemoveItemFromCategory, trackDisableItem, trackEnableItem, trackDeleteItem } = useItemActionTracking();

  const { mutate: handleRemoveFromCategory, isLoading: removingFromCategory } = useMutation(
    () => {
      if (!category) {
        return Promise.reject();
      }
      const itemBindingId = category.item_bindings.find(ib => ib.item_id === item.id)?.id;

      if (!itemBindingId) {
        throw Error(`Category ${category.id} does not contain item ${item.id}`);
      }
      return api.deleteItemBinding(item.menu_id, category.id, itemBindingId);
    },
    {
      onSuccess: updatedMenu => {
        toast.success(<FormattedMessage id="toast.success" />);
        updateMenuData({ actionType: 'updateMenu', payload: updatedMenu });
        trackRemoveItemFromCategory(item.id, category?.id ?? '');
      },
      onError: (error: any) => {
        if (!isAxiosError(error)) {
          throw error;
        }
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutate: handleDisable, isLoading: disabling } = useMutation(
    () => api.updateItemEnabled(item.menu_id, item.id, false),
    {
      onMutate: () => reportItemEnabledToggle(false),
      onSuccess: updatedItem => {
        toast.success(<FormattedMessage id="toast.success" />);
        updateMenuData({ actionType: 'updateItem', payload: updatedItem });
        trackDisableItem(item.id);
      },
      onError: (error: any) => {
        if (!isAxiosError(error)) {
          throw error;
        }
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutate: handleEnable, isLoading: enabling } = useMutation(
    () => api.updateItemEnabled(item.menu_id, item.id, true),
    {
      onMutate: () => reportItemEnabledToggle(true),
      onSuccess: updatedItem => {
        toast.success(<FormattedMessage id="toast.success" />);
        updateMenuData({ actionType: 'updateItem', payload: updatedItem });
        trackEnableItem(item.id);

        if (onEnableSuccess) {
          onEnableSuccess();
        }
      },
      onError: (error: any) => {
        if (!isAxiosError(error)) {
          throw error;
        }
        if (onEnableFail) {
          onEnableFail();
        }
        toast.error(<ErrorToastContent error={error} />);
      },
    },
  );

  const { mutate: handleDelete, isLoading: deleting } = useMutation(() => api.deleteItem(item.menu_id, item.id), {
    onMutate: () => {
      reportItemDelete();
    },
    onSuccess: async () => {
      // From legacy saga: "There might've been an item binding that would've gotten deleted now, so we should read fresh state of the menu"
      const updatedMenu = await api.readMenu(item.menu_id);
      const itemIndex = items.findIndex(i => i.id === item.id);
      const updatedItems = remove(itemIndex, 1, items);

      updateMenuData({
        actionType: 'updateMenuContainer',
        payload: { options, items: updatedItems, menu: updatedMenu },
      });
      toast.success(<FormattedMessage id="menu-item.deleteItemSuccess" />);
      trackDeleteItem(item.id);
    },
    onError: () => {
      toast.error(<FormattedMessage id="errors.deleteItemFailed" />, { duration: Infinity });
    },
    onSettled: () => setIsDeleteModalOpen(false),
  });

  const handleToggle = useCallback(() => {
    if (!!selected) {
      onDeselect && onDeselect(item.id);
    } else {
      onSelect && onSelect(item.id);
    }
  }, [onDeselect, onSelect, selected, item.id]);

  const existingImageUrls = useMemo(() => {
    return transformImagesApiToFormValues(item).map(image => image.imageUrl);
  }, [item]);

  const imageToShow = useMemo(
    () =>
      item.images.length > 0
        ? item.images[0]
        : item.image
          ? { url: item.image, blurhash: item.image_blurhash ?? null }
          : null,
    [item],
  );

  return (
    <HeaderImageUpload
      existingImageUrls={existingImageUrls}
      item={item}
      menuId={item.menu_id}
      onUpdateMenuData={(updatedItem: Item) => updateMenuData({ actionType: 'updateItem', payload: updatedItem })}
      type="venue-menu"
    >
      {({ imageHoverActive, uploadingImages }) => (
        <>
          <Root
            className={className}
            busy={deleting || enabling || disabling || removingFromCategory}
            style={style}
            onClick={e => onClick?.(e, item.id)}
            ref={rootRef}
          >
            <Header hidden={hidden} active={active} selected={selected}>
              <Left hasMargin={!onSelect}>
                {onSelect && (
                  <StyledWideAreaCheckbox
                    selectionStatus={!!selected ? 'selected' : 'not-selected'}
                    onToggle={handleToggle}
                    disabled={disabledSelection || !menuPermissions.contentEdit}
                  />
                )}
                <ThumbnailContainer>
                  {(imageHoverActive || uploadingImages) && <HeaderImageUploadThumbnail uploading={uploadingImages} />}
                  {imageToShow ? (
                    <LazyLoadedThumbnail alt={name.value} blurhash={imageToShow.blurhash} src={imageToShow.url} />
                  ) : (
                    <EmptyThumbnail />
                  )}
                </ThumbnailContainer>
                <NameAndDescription>
                  <NameAndTags>
                    <Name dir="auto" lang={name.lang}>
                      {name.value}
                    </Name>
                    <StyledTags
                      availabilityPeriod={item.availability_period}
                      disabled={!item.enabled}
                      soldOut={item.inventory_mode === 'forced_out_of_stock'}
                      primaryLanguage={primaryLanguage}
                      purchasabilityErrors={purchasabilityErrors}
                      special={category?.id === WOLT_SPECIALS_CATEGORY_ID}
                      isTiny={isNarrowView}
                    />
                    {!isBinaryInventory && itemInventoryCount && (
                      <Quantity>
                        {!item.sell_by_weight_config && (
                          <>
                            {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                            <FormattedMessage id="menu-item.available" />
                          </>
                        )}
                        {item.sell_by_weight_config && item.sell_by_weight_config.input_type === 'grams' && (
                          <>
                            <WeightBasedItemAvailableQuantity itemInventoryCount={itemInventoryCount} />{' '}
                            <FormattedMessage id="menu-item.available" />
                          </>
                        )}
                        {item.sell_by_weight_config && item.sell_by_weight_config?.input_type !== 'grams' && (
                          <>
                            {itemInventoryCount.available}/{itemInventoryCount.balance}{' '}
                            <FormattedMessage id="menu-item.available" />
                          </>
                        )}
                      </Quantity>
                    )}
                    {isNarrowView && <ItemPrice item={item} />}
                  </NameAndTags>
                  {!isNarrowView && description && (
                    <Description dir="auto" lang={description.lang}>
                      {description.value}
                    </Description>
                  )}
                </NameAndDescription>
              </Left>
              <Right>
                {!isNarrowView && <ItemPrice style={{ marginInlineStart: 16 }} item={item} />}
                <ItemActions
                  category={category}
                  item={item}
                  onCopy={onCopy}
                  onEnable={handleEnable}
                  onDelete={() => setIsDeleteModalOpen(true)}
                  onDisable={handleDisable}
                  onRemoveFromCategory={handleRemoveFromCategory}
                />
              </Right>
            </Header>
          </Root>
          {isDeleteModalOpen && (
            <ConfirmModal
              header={<FormattedMessage id="menu-item.deleteItem" />}
              loading={deleting}
              onCancel={() => setIsDeleteModalOpen(false)}
              onConfirm={handleDelete}
              open
            />
          )}
        </>
      )}
    </HeaderImageUpload>
  );
};

type ItemPriceProps = {
  item: Item;
  style?: CSSProperties;
};

export const ItemPrice = React.memo(({ item, style }: ItemPriceProps) => {
  const { getLocalizedUnit } = useLocaleUtils();

  return (
    <PriceWrapper style={style}>
      {item.discounted === null ? (
        <Price>
          <MonetaryAmount amount={parseFloat(item.baseprice.amount)} currency={item.baseprice.currency} />
          {!!item.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
        </Price>
      ) : (
        <>
          <DiscountPrice>
            <MonetaryAmount amount={parseFloat(item.baseprice.amount)} currency={item.baseprice.currency} />
            {!!item.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          </DiscountPrice>
          <OriginalPrice>
            <MonetaryAmount
              amount={parseFloat(item.discounted.original_price.amount)}
              currency={item.discounted.original_price.currency}
            />
            {!!item.sell_by_weight_config && `/${getLocalizedUnit('kilogram')}`}
          </OriginalPrice>
        </>
      )}
    </PriceWrapper>
  );
});

export default React.memo(ItemHeader);
